import { useAuth } from "@dl/web-core-js/auth";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Badge } from "@dl/web-core-js/ui";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { revisionLabel } from "../../../api/manufacturing";
import { ProductionOrderClient, type OrderBomRowDTO, type ProductionOrderDTO } from "../../../api/production";
import { OpenRecord } from "../../../components/OpenRecord";
import { when } from "./lotModel";

/**
 * The engineering BOMs the order was made from (0073): its part's revision
 * and every one its materials were exploded through (the semi-finished's,
 * each cut piece's), as recorded when its materials were written. A revision
 * replaced or deleted since stays here, so what went into this product is
 * known after the bills change. A part taken from stock is its own order's.
 */
export function EngineeringBomsTab({ lot }: { lot: ProductionOrderDTO }) {
  const { t, i18n } = useTranslation();
  const { http } = useAuth();
  const boms = useQuery({
    queryKey: ["production", "production-order", lot.id, "engineering-boms"],
    queryFn: () => new ProductionOrderClient(http).engineeringBoms(lot.id),
  });

  const cols = useMemo(() => {
    const c = defineColumns<OrderBomRowDTO>();
    return [
      c.custom("part", (row) => row.part_number, (row) => (
        <span className="font-mono" style={{ paddingLeft: `${String(row.level * 16)}px` }}>{row.part_number}</span>
      ), { header: t("lot.boms.part", "Part"), priority: 1 }),
      c.text("name", (row) => row.part_name, { header: t("lot.boms.name", "Name"), priority: 2 }),
      c.text("revision", (row) => revisionLabel(row.revision_at, row.revision), { header: t("lot.boms.revision", "Revision"), priority: 1, filter: "none" }),
      c.custom("state", (row) => (row.bom_id == null ? "deleted" : row.is_current ? "current" : "replaced"), (row) =>
        row.bom_id == null ? (
          <Badge size="sm" variant="neutral">{t("lot.boms.deleted", "Deleted since")}</Badge>
        ) : row.is_current ? (
          <Badge size="sm" variant="success">{t("lot.boms.current", "Current")}</Badge>
        ) : (
          <Badge size="sm" variant="warning">{t("lot.boms.replaced", "Replaced since")}</Badge>
        ), { header: t("lot.boms.state", "Today"), priority: 2, filter: "select" }),
      c.custom("source", (row) => row.source, (row) =>
        row.source === "BACKFILL" ? (
          <span className="text-ink-muted" title={t("lot.boms.backfill.hint", "The order was open when nexpim began recording bills: this is the bill active then, not necessarily the one its materials came from.")}>
            {t("lot.boms.backfill", "Recorded after the fact")}
          </span>
        ) : (
          t("lot.boms.snapshot", "With its materials")
        ), { header: t("lot.boms.source", "Recorded"), priority: 3 }),
      c.text("recorded_at", (row) => when(row.recorded_at, i18n.language), { header: t("lot.boms.when", "When"), priority: 3 }),
    ];
  }, [t, i18n.language]);

  return (
    <DataTable<OrderBomRowDTO>
      data={boms.data ?? []}
      columns={cols}
      loading={boms.isLoading}
      getRowId={(row) => String(row.part_id)}
      hidePrint
      toolbarLead={
        <span className="text-[12px] text-ink-muted">
          {t("lot.boms.lead", "The bills this order was made from: its own and every one below it, as they were when its materials were written.")}
        </span>
      }
      rowActions={(row) => <OpenRecord target={{ kind: "part", id: row.part_id, tab: "bom" }} label={row.part_number} />}
      emptyMessage={
        lot.sigm_project_id != null
          ? t("lot.boms.empty.sigm", "SIGM kept no revisions: nothing says which bill this order was made from.")
          : t("lot.boms.empty", "No bill recorded: the order has no materials written yet.")
      }
    />
  );
}
